import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from './firebaseConfig';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Bot, MoreHorizontal, X, Eye, MessageSquarePlus } from 'lucide-react';
import { Helmet } from 'react-helmet';
import Logo from './components/Logo';
import './LinkedInTheme.css';

function DigitalTwinChat() {
  const { vtcId } = useParams();
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vtcProfile, setVtcProfile] = useState(null);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Focus the input field when the component mounts and after the AI responds.
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    const fetchVtcProfile = async () => {
      setIsLoading(true);
      try {
        const vtcDocRef = doc(db, 'talentCards', vtcId);
        const vtcDoc = await getDoc(vtcDocRef);
        if (vtcDoc.exists()) {
          const data = vtcDoc.data();
          setVtcProfile(data); // Store the full profile
          
          // Initial greeting from the AI twin
          setHistory([{
            role: 'model',
            parts: [{ text: t('digitalTwin.greeting', { name: data.name }) }]
          }]);
        } else {
          setError(t('digitalTwin.errors.notFound'));
        }
      } catch (err) {
        setError(t('digitalTwin.errors.loadError'));
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (vtcId) {
      fetchVtcProfile();
    }
  }, [vtcId, t]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = { role: 'user', parts: [{ text: message }] };
    const newHistory = [...history, userMessage];
    setHistory(newHistory);
    
    if (!hasStartedConversation) {
        try {
             const vtcDocRef = doc(db, 'talentCards', vtcId);
             await updateDoc(vtcDocRef, {
                 chatCount: increment(1)
             });
             setHasStartedConversation(true);
        } catch (e) {
             console.error("Error incrementing chat count", e);
        }
    }

    setMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const agentUrl = process.env.REACT_APP_PUBLIC_CHAT_PROXY_URL;
      if (!agentUrl) {
        throw new Error(t('digitalTwin.errors.notConfigured'));
      }

      const response = await fetch(`${agentUrl}/public-chat-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vtc_id: vtcId, history: newHistory })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || t('digitalTwin.errors.unavailable'));
      }

      const data = await response.json();
      const modelMessage = { role: 'model', parts: [{ text: data.message }] };
      setHistory(prevHistory => [...prevHistory, modelMessage]);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Placeholder avatar if none provided
  const getInitials = (name) => {
      if (!name) return 'DT';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div className="li-layout digital-twin-layout" style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f3f2ef', overflow: 'hidden', paddingTop: 0 }}>
      
      <Helmet>
        <title>{vtcProfile ? `${vtcProfile.name}'s AI Twin | POD+` : 'AI Twin | POD+'}</title>
        <meta name="description" content={vtcProfile ? `Chat with ${vtcProfile.name}'s AI AI Twin on POD+.` : 'Chat with an AI AI Twin on POD+.'} />
        <meta property="og:title" content={vtcProfile ? `${vtcProfile.name}'s AI Twin` : 'AI Twin'} />
        <meta property="og:description" content={vtcProfile ? `Check out ${vtcProfile.name}'s AI Twin on POD+.` : 'Check out this AI Twin on POD+.'} />
        <meta property="og:image" content={vtcProfile?.photoUrl || "https://pod.gclouds.co.uk/POD-1.jpg"} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="profile" />
      </Helmet>

      {/* Public Header */}
      <header style={{ height: '52px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between' }}>
          <Logo />
          <div>
              <Link to="/login" className="li-btn-outline-primary" style={{ marginRight: '12px' }}>Log In</Link>
              <Link to="/login" className="li-btn-primary">Join Now</Link>
          </div>
      </header>

      <div className="li-container" style={{ 
          flex: 1, 
          display: isMobile ? 'flex' : 'grid', 
          flexDirection: isMobile ? 'column' : 'row',
          gridTemplateColumns: '260px 1fr 300px', 
          gap: '24px', 
          paddingTop: '24px', 
          paddingBottom: '0',
          height: 'calc(100vh - 52px)', 
          overflow: 'hidden', 
          maxWidth: '1280px',
          width: '100%',
          margin: '0 auto'
      }}>

        {/* Left Column: Profile Card - Hidden on Mobile for now to focus on Chat */}
        {!isMobile && (
        <aside className="li-sidebar-left">
             <div className="li-card li-profile-card" style={{ flexShrink: 0, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0', backgroundColor: 'white' }}>
                <div className="li-card-background" style={{ backgroundColor: '#a0b4b7', height: '64px', width: '100%' }}></div>
                <div className="li-card-content" style={{ textAlign: 'center', paddingBottom: '16px', paddingTop: '24px', position: 'relative', marginTop: 0 }}>
                    {vtcProfile?.photoUrl ? (
                        <img src={vtcProfile.photoUrl} alt={vtcProfile.name} className="li-avatar" style={{ width: '72px', height: '72px', borderRadius: '50%', border: '2px solid white', position: 'absolute', top: '-36px', left: '50%', transform: 'translateX(-50%)', objectFit: 'cover', backgroundColor: 'white' }} />
                    ) : (
                        <div className="li-avatar" style={{ width: '72px', height: '72px', borderRadius: '50%', border: '2px solid white', position: 'absolute', top: '-36px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#b0b0b0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                            {getInitials(vtcProfile?.name)}
                        </div>
                    )}
                    <div className="li-identity" style={{ padding: '0 12px', marginTop: '16px' }}>
                        <h3 className="li-name" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: 'rgba(0,0,0,0.9)' }}>
                            {vtcProfile ? vtcProfile.name : 'Loading...'}
                        </h3>
                        <p className="li-headline" style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                            {vtcProfile?.title || t('digitalTwin.headerSimple')}
                        </p>
                        {vtcProfile?.ansRegistrationStatus === 'registered' && (
                             <div style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: '#e6f7ee', color: '#057642', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                                 <Bot size={14} />
                                 <span>{t('verifiedOnAns')}</span>
                             </div>
                        )}
                    </div>
                </div>
           </div>
        </aside>
        )}

        {/* Center Column: Chat Interface */}
        <main className="li-main-feed" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', minHeight: 0 }}>
            <div className="li-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '8px', border: '1px solid #e0e0e0', backgroundColor: 'white', minHeight: 0 }}>
                {/* Header */}
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '52px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                         <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#0a66c2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                           POD+
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h2 style={{ fontSize: '14px', fontWeight: '600', margin: 0, lineHeight: '1.2' }}>
                               AI Twin
                            </h2>
                            <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px', lineHeight: '1.2' }}>
                                <span style={{ width: '6px', height: '6px', backgroundColor: '#057642', borderRadius: '50%' }}></span> Active
                            </div>
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div className="chat-content-area" style={{ flex: 1, overflowY: 'auto', padding: '16px', backgroundColor: 'white' }}>
                    
                    {history.map((msg, index) => (
                      <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                         <div style={{ display: 'flex', alignItems: 'flex-end', maxWidth: '75%', gap: '8px', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                            {/* Avatar for Model */}
                            {msg.role === 'model' && (
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0a66c2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', flexShrink: 0, marginBottom: '4px' }}>
                                    {getInitials(vtcProfile?.name)}
                                </div>
                            )}

                            <div style={{ 
                                padding: '10px 16px', 
                                borderRadius: msg.role === 'user' ? '16px 16px 2px 16px' : '16px 16px 16px 2px', 
                                backgroundColor: msg.role === 'user' ? '#0a66c2' : '#f3f2ef', 
                                color: msg.role === 'user' ? 'white' : 'black',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                wordBreak: 'break-word'
                            }}>
                              <ReactMarkdown>{msg.parts[0].text}</ReactMarkdown>
                            </div>
                         </div>
                      </div>
                    ))}

                    {isLoading && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', maxWidth: '75%', gap: '8px', flexDirection: 'row' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0a66c2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', flexShrink: 0, marginBottom: '4px' }}>{getInitials(vtcProfile?.name)}</div>
                            <div style={{ padding: '10px 16px', borderRadius: '16px 16px 16px 2px', backgroundColor: '#f3f2ef', color: 'black', fontSize: '14px' }}>
                               ...
                            </div>
                        </div>
                      </div>
                    )}
                    
                    {error && (
                        <div style={{ textAlign: 'center', color: '#d11124', fontSize: '12px', marginTop: '8px' }}>
                            {error}
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', backgroundColor: 'white' }}>
                    <form onSubmit={handleSendMessage} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '8px' }}>
                        <textarea
                            ref={inputRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={t('digitalTwin.placeholder', 'Write a message...')}
                            disabled={isLoading || error}
                            style={{ 
                                flex: 1, 
                                border: '1px solid #e0e0e0', 
                                borderRadius: '20px',
                                backgroundColor: '#f9f9f9',
                                resize: 'none', 
                                outline: 'none', 
                                fontSize: '14px', 
                                fontFamily: 'inherit', 
                                minHeight: '40px',
                                maxHeight: '120px',
                                padding: '10px 12px',
                                boxSizing: 'border-box',
                                margin: 0
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage(e);
                                }
                            }}
                        />
                        <button 
                            type="submit" 
                            disabled={isLoading || !message.trim()}
                            className="li-btn-primary"
                            style={{ padding: '0 16px', fontSize: '14px', height: '40px', borderRadius: '20px', flexShrink: 0 }}
                        >
                            {t('digitalTwin.send', 'Send')}
                        </button>
                    </form>
                    {/* Mobile CTA */}
                    {isMobile && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                             <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', lineHeight: '1.4', margin: '0 0 8px 0' }}>
                                 <b>Create your AI Twin.</b><br/>
                                 Have your own AI agent working 24/7.
                             </p>
                             <Link to="/login" className="li-btn-primary" style={{ display: 'block', width: '100%', borderRadius: '20px', textDecoration: 'none', padding: '8px 0', fontSize: '14px', textAlign: 'center' }}>
                                 Get Started
                             </Link>
                        </div>
                    )}

                    <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '11px', color: '#666' }}>
                         POD+ AI Twin • {t('digitalTwin.footer.poweredBy')} ANS
                    </div>
                </div>
            </div>
        </main>

        {/* Right Column: Promotion - Hidden on Mobile */}
        {!isMobile && (
        <aside className="li-sidebar-right">
             <div className="li-card" style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                 <div style={{ marginBottom: '16px' }}>
                     <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#e8f0fe', color: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                         <Bot size={32} />
                     </div>
                     <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>{t('welcome.title')}</h3>
                     <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                         Did you know you can have your own AI agent working for you 24/7? Automate your career growth with POD+.
                     </p>
                 </div>
                 <Link to="/login" className="li-btn-primary" style={{ width: '100%', justifyContent: 'center', borderRadius: '20px' }}>
                     Get Started
                 </Link>
             </div>
             
             <div className="li-footer-links">
                 <Link to="/about">{t('footer.about')}</Link>
                 <Link to="/accessibility">{t('footer.accessibility')}</Link>
                 <Link to="/help-center">{t('footer.helpCenter')}</Link>
                 <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                 <span>{t('auth.footer.rights')}</span>
             </div>
        </aside>
        )}

      </div>
    </div>
  );
}

export default DigitalTwinChat;